import { useCallback, useState } from 'react';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useOrphanage } from '@zenbusiness/application-commons-hooks/Orphanage';
import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import { NotificationContext } from './context';
import parseError from './error-parsing';
import Notification from './Notification';

export default function NotificationProvider({ children }) {
  const { createOrphan } = useOrphanage() || {};
  const captureException = useCaptureException();
  const trackErrorDisplayed = useClickstreamCallback('errorDisplayed');
  const { formatMessage } = useIntl();
  if (!createOrphan) {
    throw new Error('NotificationProvider must be loaded within the Orphanage context provider');
  }

  const [unrecoverable, setUnrecoverable] = useState();

  if (unrecoverable) {
    throw unrecoverable;
  }

  const onError = useCallback(
    (err) => {
      let error = err;
      // Check if error is an array of errors, and if so, use the first
      if (Array.isArray(err)) {
        const [firstErr] = err;
        error = firstErr;
      }
      const {
        title: titleKey = 'shopping.toast.error.title.default',
        message: messageKey = 'shopping.toast.error.message.default',
        isRetryable,
        logWorthy
      } = parseError(error);

      if (!isRetryable) {
        // error is non-recoverable, throw it up the component tree for the error boundary
        setUnrecoverable(err);
        return;
      }

      const title = formatMessage({
        id: titleKey,
        defaultMessage: 'Something went wrong'
      });
      const subtitle = formatMessage({
        id: messageKey,
        defaultMessage: 'Please try again.'
      });

      if (logWorthy) {
        trackErrorDisplayed({
          elementText: subtitle,
          elementValue: error.extensions?.errorCode ?? 'UNKNOWN'
        });
        captureException(error);
      }

      createOrphan(<Notification subtitle={subtitle} title={title} variant="alert" />);
    },
    [captureException, createOrphan, formatMessage, trackErrorDisplayed]
  );

  const showToast = useCallback(
    (props) => {
      createOrphan(<Notification {...props} />);
    },
    [createOrphan]
  );

  const context = useMemoObject({
    onError,
    showToast
  });

  return <NotificationContext.Provider value={context}>{children}</NotificationContext.Provider>;
}
